import { DeleteConnectedPageAction } from 'components/SelectionController/models/commonActions/DeleteConnectedPageAction';
import {
  type ConnectedPage,
  Page,
  type Project,
  User,
} from 'components/framework/models';
import { deepCamelizeKeys } from 'javascripts/keyCamelizer';
import { showAlertMessage } from 'javascripts/messages';
import Reactor from 'javascripts/reactive_rails';
import { createFayeClient } from 'javascripts/faye_client';
import { type RecordId } from 'javascripts/shared-types';
import { type ConnectedPageType, type SharedPageType } from './types';
import { type DocumentTypeEnum_value } from 'components/framework/models/generated/types';

class RemoteDocument {
  connectedPage: ConnectedPage;
  sharedByUser: User;
  sharingJWT: string;
  page?: Page;
  tokenTimer: ReturnType<typeof setTimeout>;
  loading: boolean = false;
  isLoaded: boolean = false;
  deleted: boolean = false;
  sharedReactor: Reactor;

  constructor(connectedPage: ConnectedPage) {
    this.connectedPage = connectedPage;
    this.page = new Page({
      name: connectedPage.name,
      documentType: connectedPage.documentType,
    });

    this.sharedByUser = null;
    window.sharingJWT = null;
    this.tokenTimer = null;
    this.sharedReactor = null;
  }

  async load() {
    if (this.loading || this.isLoaded) return;
    this.loading = true;

    let pageId: string;
    let baseURL: string;
    let sharingJWT: string;
    let sharedAccountId: RecordId;

    // Load the information we need to connect to the remote account
    let response = await fetch(
      `/connected_pages/${this.connectedPage.id}.json`,
      {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      }
    );

    if (response.ok) {
      const json = (await response.json()) as ConnectedPageType;
      sharedAccountId = json.connected_page.connected_account_id;
      pageId = json.connected_page.connected_page_id;
      baseURL = json.base_url;
      sharingJWT = json.sharing_jwt;
    }

    if (pageId && baseURL && sharingJWT) {
      // Load page metadata from the remote account
      //
      // Include credentials so we can do real CSRF checks against the
      // token in the session
      response = await fetch(`${baseURL}/pages/${pageId}.json`, {
        headers: {
          Authorization: `Bearer ${sharingJWT}`,
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'include',
      });

      if (response.ok) {
        let properties = (await response.json()) as SharedPageType;
        properties = deepCamelizeKeys(properties, { skip: ['editor_content'] });

        // This wasn't a GraphQL request (because we don't allow
        // cross-account GraphQL requests), so it doesn't come with
        // types. Populate the model with types so it will work the
        // way the rest of the code expects.
        properties.page.__typename = 'Page';
        properties.page.description.__typename = 'Note';
        properties.page.documentType =
          properties.page.documentTypeName.toUpperCase() as DocumentTypeEnum_value;

        if (properties.page.productId) {
          properties.page.project = {
            __typename: 'Project',
            id: properties.page.productId,
          } as Project;
        }

        if (properties.page.whiteboard) {
          properties.page.whiteboard.__typename = 'Whiteboard';
        }

        const page = new Page(properties.page);

        this.sharedByUser = new User(properties.page.sharedByUser);
        window.sharingJWT = sharingJWT;
        this.page = page;

        if (sharedAccountId && properties.page.sharedReactiveToken) {
          this.sharedReactor = new Reactor(
            createFayeClient({
              url: properties.page.sharedReactiveUrl,
              tokens: { sharedReactive: properties.page.sharedReactiveToken },
            }),
            {
              sharedAccountId,
              sharedResourceId: page.id,
            }
          );
        }
        void this.refreshToken();
      } else {
        this.unload();

        let properties = (await response.json()) as SharedPageType;
        if (properties.error) {
          showAlertMessage(properties.error);
        }
      }
    } else {
      this.connectedPage = null;
    }

    this.loading = false;
    this.isLoaded = true;
  }

  async reloadCollaborators() {
    if (!this.page || !this.connectedPage) return;
    this.page.sharedPages = null;

    const baseURL = this.page.description.editorProperties.collaborationBaseURL;
    const response = await fetch(`${baseURL}/pages/${this.page.id}.json`, {
      headers: {
        Authorization: `Bearer ${window.sharingJWT}`,
        'X-Requested-With': 'XMLHttpRequest',
      },
    });

    if (response.ok) {
      let properties = (await response.json()) as SharedPageType;
      properties = deepCamelizeKeys(properties, { skip: ['editor_content'] });
      this.page.sharedPages = properties.page.sharedPages;
    }
  }

  update(connectedPage: ConnectedPage) {
    this.connectedPage = connectedPage;
    if (this.page) {
      this.page.name = this.connectedPage.name;
    }
  }

  unload() {
    this.connectedPage = null;
    window.sharingJWT = null;
    this.sharedByUser = null;
    this.page = null;
    clearTimeout(this.tokenTimer);
  }

  async delete() {
    if (this.deleted || !this.isLoaded) return;

    this.deleted = true;
    const records = [this.connectedPage];
    const action = new DeleteConnectedPageAction(records);
    await action.runForRecords(records);
  }

  async refreshToken() {
    clearTimeout(this.tokenTimer);
    this.tokenTimer = setTimeout(() => {
      void this.refreshToken();
    }, 9 * 60 * 1000);
    if (this.isLoaded) {
      let response = await fetch(
        `/connected_pages/${this.connectedPage.id}.token`,
        { headers: { 'X-Requested-With': 'XMLHttpRequest' } }
      );
      if (response.ok) {
        const json = (await response.json()) as { token: string };
        window.sharingJWT = json.token;
      }
    }
  }
}

export default RemoteDocument;
